import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";
import ErrorBoundary from "@/components/ErrorBoundary";
import AiFlagToggle from "@/components/admin/AiFlagToggle";
import { HFButton } from "@/components/ui-hf";
import {
    Sparkles, Loader2, Download, RefreshCw, Trash2, Info,
} from "lucide-react";

/**
 * AdminVorherNachher — KI-gestützter Vorher/Nachher-Bildpaar-Generator.
 *
 * Zwei-stufig: erst Vorher-Bild aus Text-Prompt, dann Nachher-Bild als
 * Image-Edit mit dem Vorher-Bild als Referenz. Damit bleibt Architektur/
 * Perspektive identisch.
 */

const VORHER_OPTIONS = [
    { value: "rohbau", label: "Rohbau" },
    { value: "sanierungsfall", label: "Sanierungsfall" },
    { value: "leerstand", label: "Leerstand" },
    { value: "unrenoviert", label: "Unrenoviert (70er/80er)" },
    { value: "custom", label: "✏️ Eigene Beschreibung…" },
];
const NACHHER_OPTIONS = [
    { value: "fertigstellung_neubau", label: "Fertigstellung Neubau" },
    { value: "premium_renovation", label: "Premium-Renovierung" },
    { value: "editorial_golden_hour", label: "Editorial Golden Hour" },
    { value: "moebliert_bewohnt", label: "Möbliert & bewohnt" },
    { value: "custom", label: "✏️ Eigene Beschreibung…" },
];

const OPTION_LABEL: Record<string, string> = Object.fromEntries(
    [...VORHER_OPTIONS, ...NACHHER_OPTIONS].map((o) => [o.value, o.label]),
);

const VORHER_CUSTOM_PLACEHOLDER =
    "z. B. verwitterte Holzfassade, Efeu bewachsen, Sturmschäden, verlassener Dachboden mit alten Möbeln";
const NACHHER_CUSTOM_PLACEHOLDER =
    "z. B. minimalistisch möbliert im skandinavischen Stil, Poolanlage mit LED-Beleuchtung bei Nacht, ökologische Holzbauweise mit Solarzellen";

type PairPart = { media_id: string; url: string; title: string; width?: number; height?: number };
type Pair = {
    id: string;
    objekt: string;
    vorher_szenario: string;
    vorher_custom_text?: string | null;
    nachher_szenario: string;
    nachher_custom_text?: string | null;
    created_at: string;
    admin_email?: string;
    vorher: PairPart | null;
    nachher: PairPart | null;
};

export default function AdminVorherNachher() {
    return (
        <ErrorBoundary testId="admin-vn-error-boundary" title="Vorher-Nachher-Generator konnte nicht geladen werden.">
            <Inner />
        </ErrorBoundary>
    );
}

/** Textarea + Zeichen-Counter + Hilfe-Text für Custom-Szenarien.
 *  Zeigt Rot ab min-Länge unterschritten oder Max überschritten. */
function CustomBox(props: {
    testIdPrefix: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    disabled?: boolean;
}) {
    const len = props.value.length;
    const tooShort = len > 0 && len < 10;
    const tooLong = len > 500;
    return (
        <div className="space-y-1">
            <textarea
                value={props.value}
                onChange={(e) => props.onChange(e.target.value)}
                placeholder={props.placeholder}
                className="w-full px-3 py-2 rounded-sm bg-white border text-sm"
                style={{ borderColor: "hsl(var(--hf-line))", minHeight: "72px", resize: "vertical" }}
                rows={3}
                maxLength={600}
                data-testid={`${props.testIdPrefix}-textarea`}
                disabled={props.disabled}
            />
            <div className="flex items-center justify-between text-[11px] text-hf-muted">
                <span data-testid={`${props.testIdPrefix}-hint`}>
                    Beschreib den Zustand konkret (DE oder EN). Je präziser, desto besser das Ergebnis.
                </span>
                <span
                    className={tooShort || tooLong ? "text-hf-danger" : ""}
                    data-testid={`${props.testIdPrefix}-counter`}
                >
                    {len} / 500
                </span>
            </div>
        </div>
    );
}

/** Kurzform des Szenarios für die Verlaufskarte. */
function scenarioBadge(sz: string, customText?: string | null): { label: string; full: string } {
    if (sz === "custom") {
        const t = (customText || "").trim();
        const short = t.length > 60 ? t.slice(0, 60) + "…" : (t || "Eigene Beschreibung");
        return { label: short, full: t || "Eigene Beschreibung" };
    }
    const label = OPTION_LABEL[sz] || sz;
    return { label, full: label };
}

function Inner() {
    const [objekt, setObjekt] = useState("");
    const [vorherSz, setVorherSz] = useState("sanierungsfall");
    const [nachherSz, setNachherSz] = useState("premium_renovation");
    const [vorherCustom, setVorherCustom] = useState("");
    const [nachherCustom, setNachherCustom] = useState("");
    const [loading, setLoading] = useState(false);
    const [stage, setStage] = useState<"idle" | "vorher" | "nachher">("idle");
    const [error, setError] = useState("");
    const [current, setCurrent] = useState<Pair | null>(null);
    const [history, setHistory] = useState<Pair[]>([]);
    const [quota, setQuota] = useState<{ used: number; limit: number }>({ used: 0, limit: 20 });
    const [regenerating, setRegenerating] = useState(false);

    const loadHistory = useCallback(async () => {
        const r = await api<{ items: Pair[]; quota: { used: number; limit: number } }>(
            "/api/admin/vorher-nachher/history?limit=20",
        );
        if (r.ok && r.data) {
            setHistory(r.data.items);
            setQuota(r.data.quota);
        }
    }, []);

    useEffect(() => { void loadHistory(); }, [loadHistory]);
    useEffect(() => {
        if (!error) return;
        const t = setTimeout(() => setError(""), 8000);
        return () => clearTimeout(t);
    }, [error]);

    const generate = useCallback(async () => {
        if (!objekt.trim() || objekt.trim().length < 3) {
            setError("Bitte das Objekt mit mindestens 3 Zeichen beschreiben.");
            return;
        }
        if (vorherSz === "custom" && vorherCustom.trim().length < 10) {
            setError("Bitte gib eine Beschreibung für das Vorher-Szenario ein (min. 10 Zeichen).");
            return;
        }
        if (nachherSz === "custom" && nachherCustom.trim().length < 10) {
            setError("Bitte gib eine Beschreibung für das Nachher-Szenario ein (min. 10 Zeichen).");
            return;
        }
        setError("");
        setLoading(true);
        setStage("vorher");
        const stageTimer = setTimeout(() => setStage("nachher"), 30_000);
        const res = await api<Pair & { quota: { used: number; limit: number } }>(
            "/api/admin/vorher-nachher/generate",
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    objekt: objekt.trim(),
                    vorher_szenario: vorherSz,
                    vorher_custom_text: vorherSz === "custom" ? vorherCustom.trim() : null,
                    nachher_szenario: nachherSz,
                    nachher_custom_text: nachherSz === "custom" ? nachherCustom.trim() : null,
                }),
            },
        );
        clearTimeout(stageTimer);
        setLoading(false);
        setStage("idle");
        if (!res.ok || !res.data) {
            setError(res.error || "Generierung fehlgeschlagen.");
            return;
        }
        setCurrent(res.data);
        if (res.data.quota) setQuota(res.data.quota);
        void loadHistory();
    }, [objekt, vorherSz, nachherSz, vorherCustom, nachherCustom, loadHistory]);

    const regenerateNachher = useCallback(async () => {
        if (!current) return;
        // Wenn User das Nachher-Szenario im Panel auf "custom" geändert hat
        // und was Eigenes eingetippt hat, das mitgeben — sonst nutzt Backend
        // die Werte aus dem alten Verlaufseintrag.
        setRegenerating(true);
        setError("");
        const res = await api<Pair & { quota: { used: number; limit: number } }>(
            `/api/admin/vorher-nachher/${current.id}/regenerate-nachher`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    nachher_szenario: current.nachher_szenario,
                    nachher_custom_text: current.nachher_szenario === "custom"
                        ? (current.nachher_custom_text || nachherCustom.trim())
                        : null,
                }),
            },
        );
        setRegenerating(false);
        if (!res.ok || !res.data) {
            setError(res.error || "Nachher-Regeneration fehlgeschlagen.");
            return;
        }
        setCurrent(res.data);
        if (res.data.quota) setQuota(res.data.quota);
        void loadHistory();
    }, [current, nachherCustom, loadHistory]);

    const deleteEntry = useCallback(async (id: string) => {
        const res = await api(`/api/admin/vorher-nachher/${id}`, { method: "DELETE" });
        if (res.ok) {
            if (current?.id === id) setCurrent(null);
            void loadHistory();
        }
    }, [current, loadHistory]);

    const downloadFile = (url: string, filename: string) => {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div data-testid="admin-vorher-nachher" className="space-y-8">
            {/* Header */}
            <div>
                <div className="hf-eyebrow">KI-Werkzeug</div>
                <h1 className="font-display text-3xl md:text-4xl mt-2">Vorher-Nachher-Generator</h1>
                <p className="mt-1 text-hf-muted max-w-2xl">
                    Erstelle motiv-identische Bildpaare via KI. Ideal für Angebots-Präsentationen,
                    Sanierungs-Storytelling und Editorial-Vorschauen.
                </p>
            </div>

            {/* Hinweisbox */}
            <div
                className="rounded-md p-4 text-sm flex gap-3"
                style={{ background: "hsl(var(--hf-gold-soft) / 0.25)", color: "hsl(var(--hf-ink))" }}
                data-testid="vn-info-box"
            >
                <Info size={18} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--hf-gold-dark))" }} />
                <div>
                    Vorher/Nachher werden per KI erzeugt. Bei ca. 10 % der Generierungen weichen die
                    Motive leicht voneinander ab — dann einfach „Nur Nachher neu generieren“ klicken.
                    Für rechtssichere Dokumentation echte Fotos verwenden.
                </div>
            </div>

            {/* Formular */}
            <section
                className="rounded-md border p-6 space-y-5"
                style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-bg))" }}
                data-testid="vn-form-panel"
            >
                <div>
                    <label className="hf-eyebrow" style={{ fontSize: "10px" }} htmlFor="vn-objekt">
                        Objekt beschreiben
                    </label>
                    <input
                        id="vn-objekt"
                        type="text"
                        value={objekt}
                        onChange={(e) => setObjekt(e.target.value)}
                        placeholder="z. B. Wohnzimmer, Pool-Gartenanlage, Villa mit Flachdach, Bauernhof"
                        className="w-full mt-2 px-3 py-2 rounded-sm bg-white border text-sm"
                        style={{ borderColor: "hsl(var(--hf-line))" }}
                        data-testid="vn-input-objekt"
                        disabled={loading}
                        maxLength={200}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="hf-eyebrow" style={{ fontSize: "10px" }} htmlFor="vn-vorher">
                            Vorher-Szenario
                        </label>
                        <select
                            id="vn-vorher"
                            value={vorherSz}
                            onChange={(e) => setVorherSz(e.target.value)}
                            className="w-full px-3 py-2 rounded-sm bg-white border text-sm"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            data-testid="vn-select-vorher"
                            disabled={loading}
                        >
                            {VORHER_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        {vorherSz === "custom" && (
                            <CustomBox
                                testIdPrefix="vn-vorher-custom"
                                value={vorherCustom}
                                onChange={setVorherCustom}
                                placeholder={VORHER_CUSTOM_PLACEHOLDER}
                                disabled={loading}
                            />
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className="hf-eyebrow" style={{ fontSize: "10px" }} htmlFor="vn-nachher">
                            Nachher-Szenario
                        </label>
                        <select
                            id="vn-nachher"
                            value={nachherSz}
                            onChange={(e) => setNachherSz(e.target.value)}
                            className="w-full px-3 py-2 rounded-sm bg-white border text-sm"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            data-testid="vn-select-nachher"
                            disabled={loading}
                        >
                            {NACHHER_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        {nachherSz === "custom" && (
                            <CustomBox
                                testIdPrefix="vn-nachher-custom"
                                value={nachherCustom}
                                onChange={setNachherCustom}
                                placeholder={NACHHER_CUSTOM_PLACEHOLDER}
                                disabled={loading}
                            />
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <HFButton
                        onClick={() => void generate()}
                        disabled={loading || quota.used >= quota.limit}
                        data-testid="vn-generate-btn"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                <span>
                                    {stage === "vorher"
                                        ? "Vorher-Bild wird erzeugt …"
                                        : "Nachher-Bild wird erzeugt …"}
                                </span>
                            </>
                        ) : (
                            <>
                                <Sparkles size={14} />
                                <span>Bildpaar generieren</span>
                            </>
                        )}
                    </HFButton>
                    <span
                        className="text-xs text-hf-muted"
                        data-testid="vn-quota"
                    >
                        Heute verwendet: <strong>{quota.used}</strong> von {quota.limit}
                    </span>
                </div>

                {error && (
                    <div
                        className="p-3 rounded text-sm flex items-start gap-3"
                        style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}
                        data-testid="vn-error"
                        role="status"
                        aria-live="polite"
                    >
                        <span className="flex-1">{error}</span>
                        <button
                            type="button"
                            onClick={() => setError("")}
                            aria-label="Meldung schließen"
                            className="shrink-0 opacity-70 hover:opacity-100 text-xs"
                        >
                            ✕
                        </button>
                    </div>
                )}
            </section>

            {/* Ergebnis */}
            {current && current.vorher && current.nachher && (
                <section data-testid="vn-result-panel" className="space-y-4">
                    <h2 className="font-display text-xl">Ergebnis</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {(["vorher", "nachher"] as const).map((k) => {
                            const p = current[k]!;
                            return (
                                <div key={k} data-testid={`vn-result-${k}`}>
                                    <div
                                        className="hf-eyebrow mb-2"
                                        style={{ color: "hsl(var(--hf-gold-dark))", fontSize: "11px" }}
                                    >
                                        {k === "vorher" ? "Vorher" : "Nachher"}
                                    </div>
                                    <img
                                        src={p.url}
                                        alt={p.title}
                                        className="w-full h-auto rounded-md border"
                                        style={{ borderColor: "hsl(var(--hf-line))" }}
                                    />
                                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                                        <HFButton
                                            onClick={() => downloadFile(p.url, `${k}-${current.id.slice(0, 8)}.jpg`)}
                                            data-testid={`vn-download-${k}`}
                                        >
                                            <Download size={13} />
                                            <span>Download</span>
                                        </HFButton>
                                        <AiFlagToggle
                                            mediaId={p.media_id}
                                            compact
                                            testIdPrefix={`vn-ai-flag-${k}`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex flex-wrap gap-3 pt-2">
                        <HFButton
                            onClick={() => void generate()}
                            disabled={loading || regenerating}
                            data-testid="vn-regenerate-both-btn"
                        >
                            <RefreshCw size={13} />
                            <span>Neu generieren (Vorher + Nachher)</span>
                        </HFButton>
                        <HFButton
                            onClick={() => void regenerateNachher()}
                            disabled={loading || regenerating}
                            data-testid="vn-regenerate-nachher-btn"
                        >
                            {regenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                            <span>Nur Nachher neu generieren</span>
                        </HFButton>
                    </div>
                </section>
            )}

            {/* Verlauf */}
            <section data-testid="vn-history-panel" className="space-y-4">
                <h2 className="font-display text-xl">Letzte Generierungen</h2>
                {history.length === 0 ? (
                    <p className="text-sm text-hf-muted" data-testid="vn-history-empty">
                        Noch keine Generierungen. Fange oben mit dem Formular an.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {history.map((h) => {
                            const vBadge = scenarioBadge(h.vorher_szenario, h.vorher_custom_text);
                            const nBadge = scenarioBadge(h.nachher_szenario, h.nachher_custom_text);
                            return (
                            <div
                                key={h.id}
                                className="rounded-md border p-3 space-y-2"
                                style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-bg))" }}
                                data-testid={`vn-history-item-${h.id}`}
                                title={`${vBadge.full} → ${nBadge.full}`}
                            >
                                <div className="grid grid-cols-2 gap-1">
                                    {h.vorher?.url && (
                                        <img
                                            src={h.vorher.url}
                                            alt="Vorher"
                                            className="w-full h-24 object-cover rounded-sm cursor-pointer"
                                            onClick={() => setCurrent(h)}
                                        />
                                    )}
                                    {h.nachher?.url && (
                                        <img
                                            src={h.nachher.url}
                                            alt="Nachher"
                                            className="w-full h-24 object-cover rounded-sm cursor-pointer"
                                            onClick={() => setCurrent(h)}
                                        />
                                    )}
                                </div>
                                <div className="text-xs text-hf-ink font-medium truncate" title={h.objekt}>
                                    {h.objekt}
                                </div>
                                <div className="text-[11px] text-hf-muted italic truncate">
                                    {vBadge.label} → {nBadge.label}
                                </div>
                                <div className="text-[11px] text-hf-muted">
                                    {fmtDateTime(h.created_at)}
                                </div>
                                <div className="flex items-center justify-between pt-1">
                                    <button
                                        type="button"
                                        onClick={() => setCurrent(h)}
                                        className="text-[11px] underline decoration-dotted"
                                        data-testid={`vn-history-open-${h.id}`}
                                    >
                                        Öffnen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void deleteEntry(h.id)}
                                        aria-label="Eintrag entfernen"
                                        className="text-hf-muted hover:text-hf-danger transition-colors"
                                        data-testid={`vn-history-delete-${h.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <p className="text-[11px] text-hf-muted pt-6" data-testid="vn-footer-cost-hint">
                Jede Generierung erzeugt 2 KI-Bilder. Rate-Limit: {quota.limit} Generierungen pro Tag pro Admin-User.
            </p>
        </div>
    );
}
